import React, { useMemo } from 'react';

// import { AchievementsCard, achievementsCardShouldMount } from './cards/AchievementsCard/AchievementsCard';
// import { NotificationsCard } from './cards/NotificationsCard/NotificationsCard';
// import { ProgramStatusCard } from './cards/ProgramStatusCard/ProgramStatusCard';
// import { TodaysFocusCard } from './cards/TodaysFocusCard/TodaysFocusCard';
// import { GlassCardGrid } from './GlassCardGrid/GlassCardGrid';
// import { GlassCardHeader } from './GlassCardHeader/GlassCardHeader';
// import { GlassWidgetCardCarousel } from './GlassWidgetCardCarousel/GlassWidgetCardCarousel';
// import { getGlassWidgetVisibleCardCount, getMiddleCarouselNeedsNav } from './glassWidgetCarouselMath';
// import { useAdvisorDashboardData } from './hooks/useAdvisorDashboardData';
// import { useGlassWidgetCompactViewport } from './hooks/useGlassWidgetCompactViewport';
// import { useNewAndNowNotifications } from './hooks/useNewAndNowNotifications';
import clsx from 'clsx';
import i18n from 'i18n-react';

import useAdvisorDashboardData from 'hooks/useAdvisorDashboardData';
import useGlassWidgetCompactViewport from 'hooks/useGlassWidgetCompactViewport';
import useNewAndNowNotifications from 'hooks/useNewAndNowNotifications';

import AchievementsCard, { achievementsCardShouldMount } from './cards/AchievementsCard/AchievementsCard';
import NotificationsCard from './cards/NotificationsCard/NotificationsCard';
import ProgramStatusCard from './cards/ProgramStatusCard/ProgramStatusCard';
import TodaysFocusCard from './cards/TodaysFocusCard/TodaysFocusCard';
import GlassCardGrid from './GlassCardGrid/GlassCardGrid';
import GlassCardHeader from './GlassCardHeader/GlassCardHeader';
import GlassWidgetCardCarousel from './GlassWidgetCardCarousel/GlassWidgetCardCarousel';
import glassWidgetCarouselMath from './glassWidgetCarouselHelpers';

/**
 * @param root0
 * @param root0.appId
 * @param root0.rewards
 * @param root0.metallicTiers
 * @param root0.position
 * @param root0.firstName
 * @param root0.enableFrostedBackground
 * @param root0.dashboardLangCode
 * @param root0.lastName
 * @param root0.email
 * @param root0.lightTheme
 */
const GlassWidgetPanel = ({
  appId,
  rewards = null,
  metallicTiers = null,
  position = 'left',
  firstName,
  lastName,
  email,
  enableFrostedBackground = false,
  dashboardLangCode = 'en-US',
  lightTheme = null,
}) => {
  const { maxVisibleGlassWidgetCardSlots: maxVisible } = useGlassWidgetCompactViewport({
    compactViewportMaxHeightPx: 899,
    compactMaxVisibleGlassCardSlots: 3,
  });

  const {
    data: advisorData,
    error: advisorError,
    isLoading: advisorLoading,
  } = useAdvisorDashboardData(appId, email, dashboardLangCode);

  const rewardsEnabled = useMemo(() => {
    if (rewards !== null && rewards !== undefined) return Boolean(rewards);
    const v = advisorData?.feature_flags?.rewards;
    return v === 1 || v === true;
  }, [rewards, advisorData?.feature_flags?.rewards]);

  const metallicTiersEnabled = useMemo(() => {
    if (metallicTiers !== null && metallicTiers !== undefined) return Boolean(metallicTiers);
    const v = advisorData?.feature_flags?.metallic_tiers;
    return v === 1 || v === true;
  }, [metallicTiers, advisorData?.feature_flags?.metallic_tiers]);

  const lightThemeEnabled = useMemo(() => {
    if (lightTheme !== null && lightTheme !== undefined) return Boolean(lightTheme);
    const v = advisorData?.feature_flags?.enable_advisor_dashboard_light_theme;
    return v === 1 || v === true;
  }, [lightTheme, advisorData?.feature_flags?.enable_advisor_dashboard_light_theme]);

  const {
    notifications: newAndNowNotifications,
    isLoading: newAndNowLoading,
    error: newAndNowError,
  } = useNewAndNowNotifications(appId);

  const profileImgUrl = useMemo(() => {
    const u = advisorData?.profile_picture_url;
    return typeof u === 'string' && u.trim() !== '' ? u.trim() : null;
  }, [advisorData]);

  const programStatusUserInfo = useMemo(() => {
    const emailNew = typeof email === 'string' ? email.trim() : '';
    return {
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      isLoggedIn: emailNew !== '',
      profileFrameUrl:
        email !== '' && appId >= 0
          ? `https://accounts.travpromobile.com/profile/?header=false&email=${encodeURIComponent(
            email,
          )}&app_id=${appId}`
          : '',
    };
  }, [appId, email, firstName, lastName]);

  const focusMessages = useMemo(() => advisorData?.focus?.messages ?? [], [advisorData?.focus?.messages]);

  const focusPanelData = useMemo(
    () => ({
      isLoading: advisorLoading,
      error: advisorError,
      messages: focusMessages,
    }),
    [advisorLoading, advisorError, focusMessages],
  );

  const achievementsPanelData = useMemo(
    () => ({
      loading: advisorLoading,
      loadError: advisorError,
      rewards: advisorData?.rewards ?? null,
    }),
    [advisorLoading, advisorError, advisorData?.rewards],
  );

  const showTodaysFocusCard = advisorLoading || advisorError != null || focusMessages.length > 0;

  const showNotificationsCard = newAndNowLoading || newAndNowError != null || newAndNowNotifications.length > 0;

  const includeAchievementsCard = useMemo(
    () => rewardsEnabled && achievementsCardShouldMount(achievementsPanelData),
    [rewardsEnabled, achievementsPanelData],
  );

  const visibleCardCount = useMemo(
    () =>
      glassWidgetCarouselMath.getGlassWidgetVisibleCardCount({
        showTodaysFocus: showTodaysFocusCard,
        showProgramStatus: true,
        showAchievements: includeAchievementsCard,
        showNotifications: showNotificationsCard,
      }),
    [showTodaysFocusCard, includeAchievementsCard, showNotificationsCard],
  );

  const pinnedTop = useMemo(() => {
    if (!showTodaysFocusCard) return [];
    return [<TodaysFocusCard key="glass-todays-focus" variant="accent" focus={focusPanelData} lightTheme={lightThemeEnabled} />];
  }, [showTodaysFocusCard, focusPanelData, lightThemeEnabled]);

  const pinnedBottom = useMemo(() => {
    if (!showNotificationsCard) return [];
    return [
      <NotificationsCard
        key={newAndNowNotifications.map((n) => String(n.id)).join('|')}
        appId={appId}
        notifications={newAndNowNotifications}
        isLoading={newAndNowLoading}
        error={newAndNowError}
        lightTheme={lightThemeEnabled}
      />,
    ];
  }, [showNotificationsCard, appId, newAndNowNotifications, newAndNowLoading, newAndNowError, lightThemeEnabled]);

  const middleSlotCardCount = 1 + (includeAchievementsCard ? 1 : 0);

  const addContentGutter = useMemo(
    () =>
      glassWidgetCarouselMath.getMiddleCarouselNeedsNav(
        maxVisible,
        pinnedTop.length + pinnedBottom.length,
        middleSlotCardCount,
      ),
    [maxVisible, pinnedTop.length, pinnedBottom.length, middleSlotCardCount],
  );

  const middleCards = useMemo(() => {
    const middle = [
      <ProgramStatusCard
        key="glass-program-status"
        addContentGutter={addContentGutter}
        metallicTiers={metallicTiersEnabled}
        advisorMilestones={advisorData?.milestones ?? null}
        profilePictureUrl={profileImgUrl}
        firstName={programStatusUserInfo.firstName}
        lastName={programStatusUserInfo.lastName}
        isLoggedIn={programStatusUserInfo.isLoggedIn}
        profileFrameUrl={programStatusUserInfo.profileFrameUrl}
        lightTheme={lightThemeEnabled}
      />,
    ];
    if (includeAchievementsCard) {
      middle.push(
        <AchievementsCard
          key="glass-achievements"
          addContentGutter={addContentGutter}
          achievements={achievementsPanelData}
          lightTheme={lightThemeEnabled}
        />,
      );
    }
    return middle;
  }, [
    addContentGutter,
    metallicTiersEnabled,
    advisorData?.milestones,
    profileImgUrl,
    programStatusUserInfo.firstName,
    programStatusUserInfo.lastName,
    programStatusUserInfo.isLoggedIn,
    programStatusUserInfo.profileFrameUrl,
    includeAchievementsCard,
    achievementsPanelData,
    lightThemeEnabled,
  ]);

  return (
    <div
      className={clsx('homeLayout__sidebar', {
        'homeLayout__sidebar--left': position === 'left',
        'homeLayout__sidebar--right': position === 'right',
      })}
    >
      <div
        className={clsx('homeLayout__sidebarRibbon', {
          'homeLayout__sidebarRibbon--frosted': enableFrostedBackground,
          'homeLayout__sidebarRibbon--elevatedCenter': visibleCardCount <= 2,
        })}
        data-visible-glass-widget-cards={visibleCardCount}
      >
        <GlassCardGrid>
          <GlassCardHeader
            title={
              firstName
                ? i18n.translate('glassWidgets.welcomeBack', { firstName })
                : i18n.translate('glassWidgets.welcome')
            }
          />
          <GlassWidgetCardCarousel
            maxVisible={maxVisible}
            pinnedTop={pinnedTop}
            pinnedBottom={pinnedBottom}
            middleCards={middleCards}
          />
          <GlassCardHeader
            className="glass-card-header--ghost"
            title={
              firstName
                ? i18n.translate('glassWidgets.welcomeBack', { firstName })
                : i18n.translate('glassWidgets.welcome')
            }
          />
        </GlassCardGrid>
      </div>
      <div className="homeLayout__sidebarGutter" aria-hidden="true" />
    </div>
  );
};

export default GlassWidgetPanel;
